<template>
  <div ref="userBar" class="user-bar">
    <button class="user-bar__menu-button" @click="onButtonClick">
      <span>≡</span>
    </button>
    <div v-if="state.open" class="user-bar__menu">
      <ol>
        <li v-if="collection" class="user-bar__menu-item">
          <NuxtLink :to="pageUrl" target="_blank" class="user-bar__link" @click="onButtonClick">
            Edit page
          </NuxtLink>
        </li>
        <li class="user-bar__menu-item">
          <NuxtLink :to="cmsUrl" target="_blank" class="user-bar__link" @click="onButtonClick">
            Go to Directus CMS
          </NuxtLink>
        </li>
        <li class="user-bar__menu-item">
          <button class="user-bar__link" @click="onLogoutClick">
            Logout {{ userName }}
          </button>
        </li>
      </ol>
    </div>
  </div>
</template>

<script setup lang="ts">
import { navigateTo, useRoute, useRuntimeConfig } from 'nuxt/app'
import { computed, onMounted, onUnmounted, reactive, ref } from 'vue'
import { useUserStore } from '~/store/user'
import { useSettingsStore } from '~/store/settings'
import { useUser } from '~/composables/useUser'

const props = defineProps<{
  id: Number,
  collection?: String,
}>()

const state = reactive({
  open: false
})

const userBar = ref()

const config = useRuntimeConfig()
const userStore = useUserStore()
const route = useRoute()
const settings = useSettingsStore()
const user = useUser()

const baseURL = config.public.cmsBaseUrl

const cmsUrl = computed(() => `${baseURL}/admin/content/`)
const pageUrl = computed(() => `${baseURL}/admin/content/${props?.collection}/${props.id}`)
const userName = computed(() => `${userStore.user?.first_name} ${userStore.user?.last_name}`)

const onLogoutClick = async () => {
  await user.logout()
  onButtonClick()
  const destination = (settings.getGlobal.live) ? window.location.href : `/authenticate?redirect=${route.path}`
  navigateTo(destination, { external: false, replace: false })
}

const onButtonClick = () => {
  state.open = !state.open
}

const onBodyClick = (e) => {
  if (state.open && userBar.value) {
    if (!(userBar.value === e.target || userBar.value.contains(e.target))) {
      state.open = false
    }
  }
}

onMounted(() => document.body.addEventListener('click', onBodyClick))
onUnmounted(() => document.body.removeEventListener('click', onBodyClick))

</script>

<style scoped lang="postcss">
.user-bar {
  z-index: 1000;
  position: absolute;

  &__menu-button {
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgb(102, 68, 255);
    border-radius: 50%;
    width: 44px;
    height: 44px;
    position: fixed;
    bottom: 20px;
    right: 20px;
    outline: 1px solid rgba(255, 255, 255, 0.35);

    &:hover {
      background-color: #5842d0;
    }

    &__inner {
      color: transparent;
      text-shadow: 0 0 0 white;
      font-family: -apple-system, "system-ui", "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      transition: background-color 125ms ease;
      font-size: 32px;
      margin-top: -4px;
    }
  }

  &__menu {
    position: fixed;
    bottom: calc(20px + 54px);
    right: 20px;
    filter: drop-shadow(0 0 0.25rem rgba(0, 0, 0, 0.3));

    &__inner {
      overflow: hidden;
      background-color: white;
      border-radius: 6px;
      font-family: -apple-system, "system-ui", "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
      font-size: 16px;
    }

    &:after {
      position: absolute;
      content: '';
      background-color: white;
      width: 10px;
      height: 10px;
      bottom: -5px;
      right: 18px;
      transform-origin: center center;
      transform: rotate(45deg);
      z-index: -1;
    }
  }

  &__menu-item {
    border-bottom: 2px solid #eee;
    width: 100%;
    color: black;

    &:hover {
      background-color: #eee;
    }

    &:last-child {
      border-bottom: unset;
    }
  }

  &__link {
    display: block;
    padding: 10px 20px;
  }
}

</style>
